import { PaginationT } from 'models/Pagination'

import { api } from '../index'

export interface CustomerImport {
  id: number
  state: string
  url_path: string
  filename: string
  row_imports_count: number
  row_imports_completed_count: number
  row_imports_failed_count: number
  created_at: string
  finalized_at: string | null
  snowflake_synced_at: string | null
  error: string | null
  // Added for DATA-4572 UI work (server-side fields from the coordinated
  // nexus PR). Optional because legacy imports predate the backend columns
  // and the server returns null for those records. `total_rows` is exposed
  // by the entity for analytics but the dashboard renders
  // `imported / failed` from the row-import counters, so we don't declare
  // it here until something consumes it.
  errors_url_path?: string | null
  errors_filename?: string | null
}

interface GetImportHistoryResponse {
  customer_imports: CustomerImport[]
  pagination?: PaginationT
}

interface GetImportHistoryParams {
  userListId: number
  page?: number
  per_page?: number
}

export const userListImportHistoryApi = api.injectEndpoints({
  endpoints: builder => ({
    getUserListImportHistory: builder.query<
      { data: CustomerImport[]; pagination: PaginationT },
      GetImportHistoryParams
    >({
      query: ({ userListId, ...params }) => ({
        url: `user_lists/${userListId}/import_history`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: GetImportHistoryResponse) => ({
        data: response.customer_imports || [],
        pagination: response.pagination || {
          total_pages: 1,
          per_page: 25,
          current_page: 1,
          total_count: response.customer_imports?.length || 0,
        },
      }),
      providesTags: (_result, _error, { userListId }) => [
        { type: 'UserList', id: `${userListId}-imports` },
      ],
    }),

    downloadCustomerImport: builder.query<string, string>({
      query: urlPath => ({
        url: 'file_uploads/customer_imports/download',
        params: { url_path: urlPath },
        responseHandler: async (response: Response) => {
          const blob = await response.blob()
          return URL.createObjectURL(blob)
        },
        cache: 'no-cache',
      }),
    }),
  }),
})

export const {
  useGetUserListImportHistoryQuery,
  useLazyDownloadCustomerImportQuery,
} = userListImportHistoryApi
